/* eslint-disable */
/**
 * webrtc.provider.js
 * Provedor WebRTC DataChannel P2P para palco local sem internet (mesma LAN/Wi-Fi).
 * Suporta topologia Host/Leader (1 para N) e Follower (1 para 1).
 */

class WebRTCRealtimeProvider {
    constructor() {
        this.role = 'idle'; // 'host' | 'client' | 'idle'
        this.connections = new Map(); // peerId -> { pc, dc }
        this.channels = {}; // channelName -> Array of handlers
        this.statusListeners = {}; // channelName -> status callback
        this.peerId = 'peer_' + Math.random().toString(36).substring(2, 9);
        // ID atribuído pelo host (clientPeerId da offer). Quando definido, é usado como
        // _sourcePeerId para que o relay do host consiga excluir o originador do reenvio.
        this.assignedPeerId = null;
        this.status = 'DISCONNECTED';
        this.msgSeq = 0;
    }

    getSenderId() {
        return this.assignedPeerId || this.peerId;
    }

    nextMessageId() {
        this.msgSeq += 1;
        return `${this.getSenderId()}:${Date.now()}:${this.msgSeq}`;
    }

    /**
     * Serializa o payload de sinalização
     */
    encodeSignal(signalData) {
        return signalData;
    }

    /**
     * Desserializa o payload de sinalização
     */
    decodeSignal(signalData) {
        if (typeof signalData === 'string') {
            try {
                return JSON.parse(signalData);
            } catch (e) {
                return null;
            }
        }
        return signalData;
    }

    /**
     * Cria configuração de RTCPeerConnection para LAN pura (sem STUN/TURN externo quando offline)
     */
    getPeerConfig() {
        return {
            iceServers: [
                { urls: 'stun:stun.l.google.com:19302' },
                { urls: 'stun:stun1.l.google.com:19302' },
                { urls: 'stun:stun2.l.google.com:19302' },
                { urls: 'stun:stun.cloudflare.com:3478' }
            ],
            iceCandidatePoolSize: 2
        };
    }

    /**
     * LÍDER / HOST: Cria uma conexão pendente para UM convidado (1 oferta = 1 guest).
     * Retorna { clientPeerId, waitOffer } onde waitOffer resolve com a oferta codificada
     * quando o ICE gathering termina (com timeout de segurança).
     */
    createPendingGuestConnection() {
        this.role = 'host';
        const clientPeerId = 'guest_' + Math.random().toString(36).substring(2, 9);
        const pc = new RTCPeerConnection(this.getPeerConfig());
        const dc = pc.createDataChannel('triad_stage_channel', { ordered: true });

        const peerRecord = { pc, dc, id: clientPeerId, isConnected: false, consumed: false };
        this.connections.set(clientPeerId, peerRecord);
        this.setupDataChannel(dc, clientPeerId);

        const waitOffer = new Promise((resolve, reject) => {
            let resolved = false;
            const finish = () => {
                if (resolved || !pc.localDescription) return;
                resolved = true;
                resolve(this.encodeSignal({
                    type: 'offer',
                    sdp: pc.localDescription.sdp,
                    hostPeerId: this.peerId,
                    clientPeerId
                }));
            };
            pc.onicecandidate = (event) => {
                if (!event.candidate) finish();
            };
            pc.onicegatheringstatechange = () => {
                if (pc.iceGatheringState === 'complete') finish();
            };
            pc.createOffer()
                .then(offer => pc.setLocalDescription(offer))
                .catch(err => {
                    this.connections.delete(clientPeerId);
                    reject(err);
                });
            setTimeout(finish, 3500);
        });

        return { clientPeerId, waitOffer };
    }

    /**
     * LÍDER / HOST: Publica uma oferta nova na sessão (rotaciona a anterior).
     */
    async publishHostOffer(encodedOffer) {
        if (!this.activePin) throw new Error('Sessão PIN não iniciada');
        const res = await fetch(`/api/stage-session/${this.activePin}/offer`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ offer: encodedOffer })
        });
        if (!res.ok) throw new Error('Falha ao publicar oferta da sessão');
    }

    /**
     * LÍDER / HOST: Inicia sessão com PIN de 4 dígitos via servidor local
     */
    async startHostPinSession(customPin = null) {
        this.role = 'host';
        const { clientPeerId, waitOffer } = this.createPendingGuestConnection();
        try {
            const encodedOffer = await waitOffer;
            const res = await fetch('/api/stage-session/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ offer: encodedOffer, pin: customPin || undefined })
            });
            const data = await res.json();
            if (data.pin) {
                this.activePin = data.pin;
                return data.pin;
            }
            throw new Error(data.error || 'Erro ao gerar PIN');
        } catch (e) {
            this.connections.delete(clientPeerId);
            throw e;
        }
    }

    /**
     * LÍDER / HOST: Após consumir uma oferta (guest conectado), prepara a próxima
     * para o seguinte convidado. Serializado para não empilhar ofertas.
     */
    rotateHostOffer() {
        if (!this.activePin || this.role !== 'host') return Promise.resolve(null);
        if (this.rotatingOffer) return this.rotatingOffer;
        this.rotatingOffer = (async () => {
            try {
                const { waitOffer } = this.createPendingGuestConnection();
                const encodedOffer = await waitOffer;
                await this.publishHostOffer(encodedOffer);
                return true;
            } catch (e) {
                console.warn('[WebRTC] Falha ao rotacionar oferta do host:', e);
                return false;
            } finally {
                this.rotatingOffer = null;
            }
        })();
        return this.rotatingOffer;
    }

    /**
     * LÍDER / HOST: Busca novas respostas de músicos que entraram com o PIN.
     * Retorna a quantidade de NOVAS conexões estabelecidas (é o que a contagem exibe).
     */
    async pollHostAnswers() {
        if (!this.activePin || this.role !== 'host') return 0;
        try {
            const res = await fetch(`/api/stage-session/${this.activePin}/answers`);
            if (!res.ok) return 0;
            const data = await res.json();
            const answers = data.answers || [];
            let connected = 0;
            for (const item of answers) {
                try {
                    const applied = await this.applyHostAnswer(item.answer);
                    if (applied) {
                        connected += 1;
                        console.log(`[WebRTC] Músico conectado via PIN com sucesso!`);
                        // Libera a próxima oferta para o seguinte convidado (1:1).
                        await this.rotateHostOffer();
                    }
                } catch (e) {
                    console.warn(`[WebRTC] Erro ao aplicar answer do músico:`, e);
                }
            }
            return connected;
        } catch (e) {
            return 0;
        }
    }

    /**
     * MÚSICO / GUEST: Conecta na sessão do líder usando apenas o PIN de 4 dígitos
     */
    async joinByPin(pin, maxAttempts = 3) {
        this.role = 'client';
        const cleanPin = pin.toString().trim();
        let lastError = null;

        // A oferta do host vale para UM convidado e rotaciona a cada join: em caso de
        // corrida (409 expired), busca a oferta atual e tenta de novo.
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
            try {
                // 1. Busca a oferta do Host pelo PIN
                const resOffer = await fetch('/api/stage-session/join', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ pin: cleanPin })
                });
                if (!resOffer.ok) {
                    let errorMsg = 'PIN não encontrado';
                    try {
                        const errData = await resOffer.json();
                        if (errData && errData.error) errorMsg = errData.error;
                    } catch (e) {}
                    throw new Error(errorMsg);
                }
                const data = await resOffer.json();
                if (!data || !data.offer) {
                    throw new Error('Sessão de palco inválida ou expirada');
                }

                // 2. Aceita a oferta e gera a resposta local
                const answer = await this.acceptGuestOffer(data.offer);

                // 3. Envia a resposta de volta ao servidor para o Líder receber
                const resAnswer = await fetch('/api/stage-session/join', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ pin: cleanPin, answer, guestId: this.getSenderId() })
                });

                if (!resAnswer.ok) {
                    const errData = await resAnswer.json().catch(() => ({}));
                    if (resAnswer.status === 409 && errData && errData.expired && attempt < maxAttempts) {
                        console.log(`[WebRTC] Oferta consumida por outro músico, buscando a atual (tentativa ${attempt + 1})...`);
                        await new Promise(r => setTimeout(r, 600));
                        continue;
                    }
                    throw new Error((errData && errData.error) || 'Erro ao confirmar conexão com o líder');
                }

                // 4. Aguarda o DataChannel abrir de verdade (toast só com conexão real).
                await this.waitForHostConnection(10000);
                return true;
            } catch (e) {
                lastError = e;
                if (attempt >= maxAttempts) break;
                // Erro de oferta inválida pode ser corrida: tenta de novo com a oferta atual.
                if (/expirad|invalid|inválid/i.test(e.message || '')) {
                    await new Promise(r => setTimeout(r, 600));
                    continue;
                }
                break;
            }
        }

        throw lastError || new Error('Erro ao conectar com o líder');
    }

    /**
     * GUEST: aguarda o DataChannel com o host abrir (com timeout).
     */
    waitForHostConnection(timeoutMs = 15000) {
        return new Promise((resolve, reject) => {
            const startedAt = Date.now();
            const timer = setInterval(() => {
                const record = this.connections.get('host');
                if (record && record.isConnected) {
                    clearInterval(timer);
                    resolve(true);
                    return;
                }
                if (Date.now() - startedAt > timeoutMs) {
                    clearInterval(timer);
                    reject(new Error('Líder não confirmou a conexão a tempo (tela dele aberta?)'));
                }
            }, 250);
        });
    }

    /**
     * LÍDER / HOST: Inicia uma sessão de palco e gera oferta SDP para novos pares
     */
    async createHostOffer() {
        this.role = 'host';
        const clientPeerId = 'guest_' + Math.random().toString(36).substring(2, 9);
        const pc = new RTCPeerConnection(this.getPeerConfig());
        const dc = pc.createDataChannel('triad_stage_channel', { ordered: true });

        const peerRecord = { pc, dc, id: clientPeerId, isConnected: false };
        this.connections.set(clientPeerId, peerRecord);

        this.setupDataChannel(dc, clientPeerId);

        return new Promise((resolve, reject) => {
            pc.onicecandidate = (event) => {
                if (!event.candidate) {
                    // ICE Gathering finalizado: oferta completa com todos os IPs locais (LAN)
                    const offerPayload = {
                        type: 'offer',
                        sdp: pc.localDescription.sdp,
                        hostPeerId: this.peerId,
                        clientPeerId
                    };
                    resolve({
                        clientPeerId,
                        encodedOffer: this.encodeSignal(offerPayload)
                    });
                }
            };

            pc.createOffer()
                .then(offer => pc.setLocalDescription(offer))
                .catch(err => {
                    this.connections.delete(clientPeerId);
                    reject(err);
                });

            // Timeout de segurança para caso o ICE demore
            setTimeout(() => {
                if (pc.localDescription) {
                    resolve({
                        clientPeerId,
                        encodedOffer: this.encodeSignal({
                            type: 'offer',
                            sdp: pc.localDescription.sdp,
                            hostPeerId: this.peerId,
                            clientPeerId
                        })
                    });
                }
            }, 2500);
        });
    }

    /**
     * LÍDER / HOST: Aplica a resposta recebida do Guest que escaneou a oferta
     */
    async applyHostAnswer(encodedAnswer) {
        const answerData = this.decodeSignal(encodedAnswer);
        if (!answerData || answerData.type !== 'answer') {
            throw new Error('Código de resposta inválido');
        }

        const peerRecord = this.connections.get(answerData.clientPeerId);
        if (!peerRecord) {
            throw new Error('Sessão para este convidado não encontrada ou expirada');
        }
        if (peerRecord.consumed) {
            // Oferta já consumida por outro convidado (resposta duplicada/tardia).
            return false;
        }

        const remoteDesc = new RTCSessionDescription({ type: 'answer', sdp: answerData.sdp });
        await peerRecord.pc.setRemoteDescription(remoteDesc);
        peerRecord.consumed = true;
        return true;
    }

    /**
     * GUEST / FOLLOWER: Recebe a oferta do Líder (via QR Code/texto) e gera a resposta
     */
    async acceptGuestOffer(encodedOffer) {
        this.role = 'client';
        const offerData = this.decodeSignal(encodedOffer);
        if (!offerData || offerData.type !== 'offer') {
            throw new Error('Código de oferta do Líder inválido');
        }

        // Limpa conexões anteriores se for cliente (também reseta assignedPeerId)
        this.closeAll();

        // Adota o ID atribuído pelo host para que o relay dele nos exclua do reenvio
        // (evita eco da própria mensagem de volta ao originador).
        if (offerData.clientPeerId) {
            this.assignedPeerId = offerData.clientPeerId;
        }

        const pc = new RTCPeerConnection(this.getPeerConfig());
        let dc = null;

        const peerRecord = { pc, dc: null, id: offerData.hostPeerId, isConnected: false };
        this.connections.set('host', peerRecord);

        return new Promise((resolve, reject) => {
            pc.ondatachannel = (event) => {
                dc = event.channel;
                peerRecord.dc = dc;
                this.setupDataChannel(dc, 'host');
            };

            pc.onicecandidate = (event) => {
                if (!event.candidate) {
                    const answerPayload = {
                        type: 'answer',
                        sdp: pc.localDescription.sdp,
                        hostPeerId: offerData.hostPeerId,
                        clientPeerId: offerData.clientPeerId
                    };
                    resolve(this.encodeSignal(answerPayload));
                }
            };

            pc.onicegatheringstatechange = () => {
                if (pc.iceGatheringState === 'complete' && pc.localDescription) {
                    resolve(this.encodeSignal({
                        type: 'answer',
                        sdp: pc.localDescription.sdp,
                        hostPeerId: offerData.hostPeerId,
                        clientPeerId: offerData.clientPeerId
                    }));
                }
            };

            const remoteDesc = new RTCSessionDescription({ type: 'offer', sdp: offerData.sdp });
            pc.setRemoteDescription(remoteDesc)
                .then(() => pc.createAnswer())
                .then(answer => pc.setLocalDescription(answer))
                .catch(reject);

            setTimeout(() => {
                if (pc.localDescription) {
                    resolve(this.encodeSignal({
                        type: 'answer',
                        sdp: pc.localDescription.sdp,
                        hostPeerId: offerData.hostPeerId,
                        clientPeerId: offerData.clientPeerId
                    }));
                }
            }, 3500);
        });
    }

    /**
     * Configura o DataChannel (envio/recebimento de mensagens compatíveis com Ably)
     */
    setupDataChannel(dc, peerKey) {
        dc.onopen = () => {
            console.log(`[WebRTC] DataChannel aberto com ${peerKey}!`);
            const peerRecord = this.connections.get(peerKey);
            if (peerRecord) peerRecord.isConnected = true;
            this.updateOverallStatus();
        };

        dc.onclose = () => {
            console.log(`[WebRTC] DataChannel fechado com ${peerKey}`);
            const peerRecord = this.connections.get(peerKey);
            if (peerRecord) peerRecord.isConnected = false;
            if (this.role === 'client') {
                this.connections.delete(peerKey);
            }
            this.updateOverallStatus();
        };

        dc.onerror = (err) => {
            console.warn(`[WebRTC] Erro no DataChannel ${peerKey}:`, err);
        };

        dc.onmessage = (event) => {
            try {
                const message = JSON.parse(event.data);
                this.routeIncomingMessage(message);
            } catch (e) {
                console.warn('[WebRTC] Mensagem inválida recebida:', event.data);
            }
        };
    }

    /**
     * Roteia mensagens recebidas para os listeners do canal registrado
     */
    routeIncomingMessage(message) {
        const { channelName, eventName, data } = message;
        if (!channelName) return;

        // Se o Líder recebe de um membro, faz relay para os outros membros
        if (this.role === 'host') {
            this.broadcastToGuests(message, message._sourcePeerId);
        }

        // 1. Ouvintes do canal exato
        let listeners = [...(this.channels[channelName] || [])];

        // 2. Fallback de palco: se for evento de playlist (ex: change-song) e o Guest estiver
        // em outra playlist aberta ou nenhuma específica, entrega para ouvintes de qualquer playlist room
        if (channelName.startsWith('playlist::room::')) {
            Object.keys(this.channels).forEach(ch => {
                if (ch.startsWith('playlist::room::') && ch !== channelName) {
                    listeners.push(...this.channels[ch]);
                }
            });
        }

        if (listeners.length === 0) return;

        listeners.forEach(handler => {
            try {
                // Preserva o __mid universal se presente no data ou message.mid
                const mid = (data && typeof data === 'object' && data.__mid) ? data.__mid : (message.mid || null);
                const payload = { ...data, event: eventName, __mid: mid };
                handler(payload);
            } catch (err) {
                console.error('[WebRTC] Erro no listener do canal:', err);
            }
        });
    }

    /**
     * Atualiza status geral e notifica os canais
     */
    updateOverallStatus() {
        let anyConnected = false;
        this.connections.forEach(peer => {
            if (peer.isConnected) anyConnected = true;
        });

        this.status = anyConnected ? 'SUBSCRIBED' : 'DISCONNECTED';

        Object.values(this.statusListeners).forEach(cb => {
            if (typeof cb === 'function') {
                cb(this.status, null);
            }
        });
    }

    /**
     * Interface compatível com Ably: subscribe
     */
    subscribe(channelName, eventName, onMessage, onStatusChange) {
        if (!this.channels[channelName]) {
            this.channels[channelName] = [];
        }

        const handler = (payload) => {
            if (eventName === '*' || payload.event === eventName) {
                onMessage(payload);
            }
        };

        this.channels[channelName].push(handler);

        if (onStatusChange) {
            this.statusListeners[channelName] = onStatusChange;
            onStatusChange(this.status, null);
        }

        return {
            unsubscribe: () => this.unsubscribe(channelName)
        };
    }

    /**
     * Interface compatível com Ably: unsubscribe
     */
    unsubscribe(channelName) {
        delete this.channels[channelName];
        delete this.statusListeners[channelName];
    }

    /**
     * Interface compatível com Ably: publish
     */
    publish(channelName, eventName, data) {
        const mid = (data && typeof data === 'object' && data.__mid) ? data.__mid : this.nextMessageId();
        const payload = {
            channelName,
            eventName,
            data,
            mid,
            _sourcePeerId: this.getSenderId(),
            timestamp: Date.now()
        };

        const jsonStr = JSON.stringify(payload);
        let sentCount = 0;

        this.connections.forEach(peer => {
            if (peer.dc && peer.dc.readyState === 'open') {
                peer.dc.send(jsonStr);
                sentCount++;
            }
        });

        return Promise.resolve({ sentCount });
    }

    /**
     * Líder retransmite para os outros pares (excluindo quem originou)
     */
    broadcastToGuests(payload, excludePeerId) {
        const jsonStr = JSON.stringify(payload);
        this.connections.forEach((peer, peerId) => {
            if (peerId !== excludePeerId && peer.dc && peer.dc.readyState === 'open') {
                peer.dc.send(jsonStr);
            }
        });
    }

    /**
     * Retorna a quantidade de pares conectados atualmente
     */
    getConnectedCount() {
        let count = 0;
        this.connections.forEach(peer => {
            if (peer.isConnected) count++;
        });
        return count;
    }

    /**
     * Desconecta da sessão de palco: fecha pares, limpa PIN e volta a idle.
     * O modo de transporte (cloud/local) é gerenciado pelo TransportManager.
     */
    disconnect() {
        this.closeAll();
        this.role = 'idle';
        this.activePin = null;
        this.rotatingOffer = null;
    }

    /**
     * Fecha todas as conexões
     */
    closeAll() {
        this.connections.forEach(peer => {
            try {
                if (peer.dc) peer.dc.close();
                if (peer.pc) peer.pc.close();
            } catch (e) {}
        });
        this.connections.clear();
        this.assignedPeerId = null;
        this.updateOverallStatus();
    }
}

export const webrtcService = new WebRTCRealtimeProvider();
export default webrtcService;
